/** Typed client for the Odie API. */

import type { OpenedFile } from './files/fileAccess'

export interface SessionInfo {
  id: string
  filename: string
  source: string
  pageCount: number
  /** Changes with every edit, undo and redo: equal means the same state. */
  revision: number
  canUndo: boolean
  canRedo: boolean
  /** Once true, Save must route through the flatten-and-verify path. */
  redacted?: boolean
  /** Save applies password protection. */
  protected?: boolean
  /** Restricted, and opened without its permissions password. */
  locked?: boolean
}

export interface Run {
  index: number
  text: string
  fontName: string
  fontFamily: string
  fontSize: number
  /** PDF user space: origin bottom-left, y increases upward. */
  x: number
  y: number
  width: number
  height: number
  rotation: number
  visible: boolean
  editable: boolean
  embedState: 'full' | 'subset' | 'none'
  /**
   * Distance along the baseline from x, in points, of each character
   * boundary (text.length + 1 entries), measured from the PDF's own glyph
   * positions. Used to put the caret where the user clicked.
   */
  charX: number[]
}

export interface PageModel {
  pageIndex: number
  /** Equal versions, identical models (see page-versions). '' for a
   * placeholder not yet loaded. */
  version?: string
  /** Size of the box the viewer renders (CropBox clipped to MediaBox). */
  width: number
  height: number
  /**
   * Lower-left corner of that box in user space. Run coordinates are absolute,
   * so subtract this to get page-relative pixels -- it is not always (0, 0),
   * and it moves when a page is cropped.
   */
  originX: number
  originY: number
  /** /Rotate, normalised to 0/90/180/270. Overlays are rotated to match. */
  rotation: number
  runs: Run[]
}

export interface PageInfo {
  index: number
  width: number
  height: number
  rotation: number
  hasText: boolean
  label: string
}

export type ObjectKind = 'text' | 'image' | 'signature'

export interface PlacedObject {
  id: string
  pageIndex: number
  kind: ObjectKind
  /** PDF user space: origin bottom-left, y upward. */
  x: number
  y: number
  width: number
  height: number
  rotation: number
  opacity: number
  text: string
  fontSize: number
  color: [number, number, number]
  hasImage: boolean
  imageName: string
}

export interface Paragraph {
  runIndices: number[]
  text: string
  /** As detected from where its lines start and end. */
  align: 'left' | 'center' | 'right' | 'justify'
  x: number
  y: number
  top: number
  right: number
}

export interface Rect {
  x0: number
  y0: number
  x1: number
  y1: number
}

export interface ContentBounds {
  pageIndex: number
  /** [x0, y0, x1, y1] of the rendered page box. */
  page: [number, number, number, number]
  /** Bounding box of drawn content, or null if nothing measurable. */
  content: [number, number, number, number] | null
  /** Fraction of page area that would be trimmed. */
  trimFraction: number
}

export interface CropResult {
  cropped: number
  destructive: boolean
  requiresFlatten: boolean
  notes: string[]
  session: SessionInfo
}

export type SplitMode = 'individual' | 'every' | 'ranges' | 'at'

export interface SplitSpec {
  mode: SplitMode
  every?: number
  /** One-based, comma-separated, e.g. "1-3, 5, 8-". */
  ranges?: string
  /** One-based page numbers to split before. */
  at?: number[]
}

export interface SplitPreview {
  files: { name: string; bytes: number }[]
  count: number
}

export interface ExtractedImageInfo {
  name: string
  pageIndex: number
  width: number
  height: number
  bytes: number
  /** "original" means the embedded bytes were handed back untouched. */
  encoding: string
  placement: [number, number, number, number] | null
  note: string
}

export interface ExtractedTextLine {
  text: string
  x: number
  y: number
  right: number
  fontSize: number
  fontFamily: string
  visible: boolean
}

export interface ExtractedTextPage {
  pageIndex: number
  text: string
  lines: ExtractedTextLine[]
  width: number
  height: number
}

export type CommentKind =
  | 'note' | 'highlight' | 'freetext'
  | 'underline' | 'strikeout' | 'ink' | 'square' | 'circle' | 'line' | 'stamp'
  | 'measure'

export type StampKind =
  | 'approved' | 'received' | 'reviewed' | 'draft' | 'confidential'
  | 'final' | 'void' | 'not-approved' | 'for-comment'

export interface Comment {
  id: string
  pageIndex: number
  kind: CommentKind
  rect: [number, number, number, number]
  contents: string
  author: string
  created: string
  modified: string
  color: [number, number, number]
  opacity: number
  icon: string
  inReplyTo: string | null
  quads: [number, number, number, number][]
  /** True when the annotation was already in the file when Odie opened it. */
  external: boolean
  points: number[]
  ink: [number, number][][]
  width: number
  arrow: boolean
  stamp: string
}

export interface Protection {
  protected: boolean
  openPassword: boolean
  restricted: boolean
  locked: boolean
  allow: Record<string, boolean>
  /** permission key -> what it means, for the dialog. */
  permissions: Record<string, string>
}

export interface PageMark {
  id?: string
  text: string
  position: string
  fontSize: number
  color: [number, number, number]
  opacity: number
  rotation: number
  margin: number
  pages: string
  behind: boolean
  batesPrefix: string
  batesSuffix: string
  batesStart: number
  batesDigits: number
  date?: string
}

export interface FindMatch {
  pageIndex: number
  segmentId: number
  start: number
  end: number
  text: string
  before: string
  after: string
  /** [x0, y0, x1, y1] in PDF user space. */
  rect: [number, number, number, number]
}

export interface ReplaceOutcome {
  pageIndex: number
  segmentId: number
  count: number
  fidelity: Fidelity
  additions: GlyphAddition[]
  overlaps: { text: string; points: number }[]
  warnings: string[]
  missingGlyphs?: string[]
}

export interface CommentThread {
  comment: Comment
  replies: Comment[]
}

export type ArrangeAction = 'front' | 'back' | 'forward' | 'backward'

export interface OcrEngineInfo {
  name: string
  available: boolean
  reason: string
  granularity: 'word' | 'line'
  /** False for Swifty: it reads text but never supplies coordinates. */
  suppliesGeometry: boolean
}

export interface OcrStatus {
  scannedPages: number[]
  engines: OcrEngineInfo[]
  geometryAvailable: boolean
  crossCheckDefault: boolean
}

export interface OcrResult {
  pageIndex: number
  lineCount: number
  text: string
  engine: string
  granularity: string
  elapsed: number
  meanConfidence: number
  imageSize: [number, number]
  flagged: string[]
  crossChecked: boolean
  textCorrections: number
  lines: { text: string; confidence: number; lowConfidence: boolean }[]
}

export interface RedactionPattern {
  name: string
  description: string
  /** True when a checksum gates the match, e.g. Luhn on a SIN. */
  checksum: boolean
}

export interface RedactionRegion {
  pageIndex: number
  /** [x0, y0, x1, y1] in PDF user space. */
  rect: [number, number, number, number]
  reason: string
  text: string
}

export interface RedactionReport {
  pageIndex: number
  regions: number
  runsDeleted: number
  runsSplit: number
  imagesModified: number
  annotationsRemoved: number
  coarse: string[]
  warnings: string[]
}

export interface RedactionVerdict {
  redacted: boolean
  checked: string[]
  leaked: string[]
  verified: boolean
}

/** A letter an edit had to add to the document, and where it came from. */
export interface GlyphAddition {
  char: string
  /** e.g. "Calibri from your computer", "Carlito, a metric match for Calibri". */
  source: string
  /** True when the letter came from the document's actual font. */
  exact: boolean
  note: string
  method: 'extend' | 'overlay'
}

/**
 * - exact: every letter was already in the font.
 * - extended: letters were added from the document's real font -- still exact.
 * - substituted: at least one letter came from a lookalike.
 * - failed: nothing available can draw it.
 */
export type Fidelity = 'exact' | 'extended' | 'substituted' | 'failed'

export interface EditReport {
  fidelity: Fidelity
  runIndex: number
  oldText: string
  newText: string
  widthDelta: number
  missingGlyphs: string[]
  fontModified: boolean
  warnings: string[]
  additions: GlyphAddition[]
  /**
   * Text the edit now runs into, nearest first. Text is never reflowed, so a
   * wider edit can overlap what follows it on the line; this is empty unless
   * it actually does.
   */
  overlaps?: { text: string; points: number }[]
}

export interface Probe {
  fidelity: Fidelity
  missingGlyphs: string[]
  additions: GlyphAddition[]
  unresolved: string[]
  /** Set when the user's own copy of the font would do better. */
  localFont: { postscriptNames: string[]; family: string; style: string } | null
  fontFamily?: string
  embedState?: string
}

// --- forms, file size, bookmarks, links -------------------------------------

export type FieldKind = 'text' | 'checkbox' | 'radio' | 'combo' | 'list' | 'button' | 'signature'

export interface FormWidget {
  pageIndex: number
  rect: [number, number, number, number]
  /** The state a checkbox or radio button widget turns on. */
  onState: string | null
  hidden: boolean
}

export interface FormField {
  name: string
  kind: FieldKind
  /** Text: string. Checkbox/radio: the state that is on, or null. List: string[] if multiselect. */
  value: string | string[] | null
  default: string | null
  tooltip: string
  options: { export: string; display: string }[]
  readOnly: boolean
  required: boolean
  multiline: boolean
  password: boolean
  comb: boolean
  maxLength: number | null
  editable: boolean
  multiselect: boolean
  align: number
  fontSize: number
  widgets: FormWidget[]
}

export interface FormState {
  fields: FormField[]
  xfa: boolean
}

export type CompressLevel = 'lossless' | 'standard' | 'small'

export interface CompressReport {
  before: number
  after: number
  level: CompressLevel
  imagesDownsampled: number
  imagesMerged: number
  removed: string[]
  session: SessionInfo
}

export interface Bookmark {
  /** Position in the tree as last read; lets a kept bookmark keep its style. */
  id?: string | null
  title: string
  pageIndex: number | null
  top?: number | null
  uri?: string | null
  open: boolean
  children: Bookmark[]
}

export interface PdfLink {
  id: string
  pageIndex: number
  rect: [number, number, number, number]
  targetPage: number | null
  top: number | null
  uri: string | null
  /** Safe to follow: a page here, or a web, mail or phone link. */
  safe: boolean
}

// --- rewrap, signatures, compare, ask ----------------------------------------

export interface RewrapReport {
  linesBefore: number
  linesAfter: number
  align: string
  warnings: string[]
  additions: GlyphAddition[]
}

export interface SignatureInfo {
  field: string
  signer: string
  email: string
  issuer: string
  signedAt: string | null
  reason: string
  location: string
  pageIndex: number | null
  rect: [number, number, number, number] | null
  intact: boolean
  valid: boolean
  trusted: boolean
  coverage: 'entire' | 'allowed' | 'changed'
  expired: boolean
  /** valid: sound and trusted; unknown: sound, issuer not trusted here; invalid. */
  status: 'valid' | 'unknown' | 'invalid'
  problems: string[]
}

export interface SignatureReport {
  signatures: SignatureInfo[]
  /** Unsaved changes: saving them invalidates the signatures. */
  changedSince: boolean
  emptyFields: { name: string; pageIndex: number | null; rect: [number, number, number, number] }[]
}

export interface CompareBoxes { pageIndex: number; rects: [number, number, number, number][] }

export interface CompareChange {
  id: number
  kind: 'inserted' | 'deleted' | 'replaced'
  oldText: string
  newText: string
  old: CompareBoxes[]
  new: CompareBoxes[]
  oldAnchor: { pageIndex: number; rect: [number, number, number, number] } | null
  newAnchor: { pageIndex: number; rect: [number, number, number, number] } | null
  oldPage: number | null
  newPage: number | null
}

export interface CompareResult {
  changes: CompareChange[]
  summary: {
    inserted: number; deleted: number; replaced: number; changes: number; truncated: boolean
    oldPages: number; newPages: number; identical: boolean
  }
  /** Which side is the older version. */
  oldIs: 'other' | 'current'
  otherName: string
}

export type AskEvent =
  | { type: 'meta'; pages: number[]; totalPages: number; truncated: boolean; model: string }
  | { type: 'delta'; text: string }
  | { type: 'done' }
  | { type: 'error'; message: string }

// --- measuring, building forms, accessibility --------------------------------

export interface PageScale {
  ratio: string
  unit: string
  /** Real units per PDF point. */
  factor: number
  source: 'odie' | 'document'
  bbox: [number, number, number, number] | null
  name: string
}

export type MeasureKind = 'distance' | 'perimeter' | 'area'

export type FieldBuildKind = 'text' | 'checkbox' | 'radio' | 'combo' | 'list' | 'signature'

export interface NewField {
  pageIndex: number
  kind: FieldBuildKind
  rect: [number, number, number, number]
  name?: string
  tooltip?: string
  options?: string[]
  export?: string
  fontSize?: number
  multiline?: boolean
  border?: boolean
  fill?: boolean
}

export interface FieldSuggestion {
  kind: FieldBuildKind
  rect: [number, number, number, number]
  name: string
  reason: string
}

export interface AccessCheck {
  id: string
  title: string
  status: 'passed' | 'failed' | 'warning' | 'manual'
  detail: string
  fix: '' | 'autotag' | 'properties' | 'tabs' | 'ocr' | 'alt' | 'tooltips' | 'bookmarks'
  pages: number[]
  count: number
}

export interface AccessReport {
  checks: AccessCheck[]
  tagged: boolean
  title: string
  language: string
}

export interface StructureBlock {
  id: string
  type: string
  pageIndex: number | null
  box: [number, number, number, number] | null
  text: string
  alt: string | null
  depth: number
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Supplies the Entra access token; set once MSAL has initialised. */
let tokenProvider: () => Promise<string | null> = async () => null

export function setTokenProvider(provider: () => Promise<string | null>): void {
  tokenProvider = provider
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await tokenProvider()
  const headers = new Headers(init.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const response = await fetch(path, { ...init, headers })

  if (!response.ok) {
    let detail: unknown
    let message = `${response.status} ${response.statusText}`
    try {
      const body = await response.json()
      detail = body?.detail ?? body
      if (typeof detail === 'string') message = detail
      // Structured refusals (a permission, a password) carry their own
      // sentence; without this the user saw "403 Forbidden".
      else if (typeof (detail as { message?: unknown })?.message === 'string')
        message = (detail as { message: string }).message
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(response.status, message, detail)
  }

  if (response.status === 204) return undefined as T
  const type = response.headers.get('content-type') ?? ''
  return (type.includes('application/json')
    ? await response.json()
    : await response.blob()) as T
}

/**
 * Always resolve to a Blob, whatever the content type.
 *
 * `request` auto-parses an `application/json` body, which is right for API
 * calls and wrong for a *download* that happens to be JSON -- the caller gets
 * a parsed object where it expected bytes, and URL.createObjectURL throws
 * somewhere the error handler cannot see it.
 */
async function blob(path: string, init: RequestInit = {},
                    onHeaders?: (headers: Headers) => void): Promise<Blob> {
  const token = await tokenProvider()
  const headers = new Headers(init.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const response = await fetch(path, { ...init, headers })
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`
    let detail: unknown
    try {
      const body = await response.json()
      detail = body?.detail ?? body
      if (typeof detail === 'string') message = detail
      else if (typeof (detail as { message?: unknown })?.message === 'string')
        message = (detail as { message: string }).message
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(response.status, message, detail)
  }
  onHeaders?.(response.headers)
  return await response.blob()
}

function json<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export const api = {
  health: () => request<{ status: string; authRequired: boolean }>('/api/health'),

  async open(file: OpenedFile, password = ''): Promise<SessionInfo> {
    const form = new FormData()
    form.append('file', file.blob, file.name)
    form.append('source', file.source)
    if (password) form.append('password', password)
    if (file.driveItemId) form.append('driveItemId', file.driveItemId)
    return request<SessionInfo>('/api/documents/open', {
      method: 'POST',
      body: form,
    })
  },

  describe: (id: string) => request<SessionInfo>(`/api/documents/${id}`),

  page: (id: string, index: number) =>
    request<PageModel>(`/api/documents/${id}/pages/${index}`),

  /** Every page's version, so a refresh fetches only the models that changed. */
  pageVersions: (id: string) =>
    request<{ versions: string[] }>(`/api/documents/${id}/page-versions`),

  probe: (id: string, pageIndex: number, runIndex: number, text: string) =>
    request<Probe>(`/api/documents/${id}/probe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pageIndex, runIndex, text }),
    }),

  edit: (
    id: string,
    pageIndex: number,
    runIndex: number,
    newText: string,
    preserveAdvance = false,
  ) =>
    request<{ report: EditReport; session: SessionInfo }>(
      `/api/documents/${id}/edit`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pageIndex, runIndex, newText, preserveAdvance }),
      },
    ),

  /** Hand the server a font from this computer, for this session only. */
  /** ``role: 'default'`` makes it the session's font for new text. */
  uploadFont(id: string, font: Blob, fontName: string, role: '' | 'default' = '') {
    const form = new FormData()
    form.append('file', font, 'font')
    form.append('fontName', fontName)
    form.append('role', role)
    return request<{ accepted: string; fonts: string[]; defaultFont: string | null }>(
      `/api/documents/${id}/fonts`, { method: 'POST', body: form },
    )
  },

  undo: (id: string) =>
    request<SessionInfo>(`/api/documents/${id}/undo`, { method: 'POST' }),

  redo: (id: string) =>
    request<SessionInfo>(`/api/documents/${id}/redo`, { method: 'POST' }),

  /** For the viewer: never encrypted, so pdf.js can render it. */
  file: (id: string) => blob(`/api/documents/${id}/file`),

  /** For Save: with the document's password protection applied. */
  fileForSave: (id: string) => blob(`/api/documents/${id}/file?save=true`),

  // --- protection ----------------------------------------------------------

  security: (id: string) => request<Protection>(`/api/documents/${id}/security`),

  setSecurity: (
    id: string,
    body: { remove?: boolean; openPassword?: string; permissionsPassword?: string; allow?: Record<string, boolean> },
  ) => json<{ security: Protection; session: SessionInfo }>(`/api/documents/${id}/security`, body),

  unlock: (id: string, password: string) =>
    json<{ security: Protection; session: SessionInfo }>(
      `/api/documents/${id}/security/unlock`, { password }),

  // --- headers, footers, watermarks ---------------------------------------------

  marks: (id: string) =>
    request<{ marks: PageMark[]; positions: string[]; tokens: string[] }>(`/api/documents/${id}/marks`),

  setMarks: (id: string, marks: PageMark[]) =>
    request<{ marks: PageMark[]; session: SessionInfo }>(`/api/documents/${id}/marks`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ marks }),
    }),

  // --- find and replace -----------------------------------------------------------

  search: (id: string, query: string, matchCase: boolean, wholeWord: boolean) =>
    json<{ matches: FindMatch[]; count: number }>(`/api/documents/${id}/search`,
      { query, matchCase, wholeWord }),

  replace: (
    id: string,
    body: {
      query: string; replacement: string; matchCase: boolean; wholeWord: boolean
      only?: { pageIndex: number; segmentId: number; start: number }[]
    },
  ) => json<{ replaced: number; results: ReplaceOutcome[]; session: SessionInfo }>(
    `/api/documents/${id}/replace`, body),

  // --- pages -------------------------------------------------------------

  pageList: (id: string) =>
    request<{ pages: PageInfo[]; session: SessionInfo }>(
      `/api/documents/${id}/pages`,
    ),

  deletePages: (id: string, indices: number[]) =>
    json<{ removed: number; session: SessionInfo }>(
      `/api/documents/${id}/pages/delete`, { indices },
    ),

  reorderPages: (id: string, order: number[]) =>
    json<{ session: SessionInfo }>(`/api/documents/${id}/pages/reorder`, { order }),

  rotatePages: (id: string, indices: number[], degrees: number) =>
    json<{ rotated: number; session: SessionInfo }>(
      `/api/documents/${id}/pages/rotate`, { indices, degrees },
    ),

  /** ``passwords`` holds one per file ("" for none), for protected files. */
  /** Images and PDFs, in order, as one new PDF (see files/createPdf.ts). */
  convert: (files: File[]) => {
    const form = new FormData()
    files.forEach((f) => form.append('files', f, f.name))
    return blob('/api/documents/convert', { method: 'POST', body: form })
  },

  merge: (id: string, files: File[], at?: number, passwords: string[] = []) => {
    const form = new FormData()
    files.forEach((f) => form.append('files', f, f.name))
    if (at !== undefined) form.append('at', String(at))
    form.append('passwords', JSON.stringify(passwords))
    return request<{ added: number; session: SessionInfo }>(
      `/api/documents/${id}/pages/merge`, { method: 'POST', body: form },
    )
  },

  extract: (id: string, indices: number[]) =>
    blob(`/api/documents/${id}/pages/extract`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ indices }),
    }),

  // --- placed objects ----------------------------------------------------

  objects: (id: string) =>
    request<{ objects: PlacedObject[] }>(`/api/documents/${id}/objects`),

  addText: (
    id: string,
    body: { pageIndex: number; x: number; y: number; text: string; fontSize?: number; rotation?: number },
  ) =>
    json<{ object: PlacedObject; session: SessionInfo }>(
      `/api/documents/${id}/objects/text`, body,
    ),

  addTypedSignature: (
    id: string,
    body: { pageIndex: number; x: number; y: number; text: string; fontSize?: number },
  ) =>
    json<{ object: PlacedObject; session: SessionInfo }>(
      `/api/documents/${id}/objects/signature`, body,
    ),

  addImage: (
    id: string,
    file: Blob,
    opts: { pageIndex: number; x: number; y: number; width?: number; kind?: ObjectKind },
  ) => {
    const form = new FormData()
    form.append('file', file, (file as File).name ?? 'image.png')
    form.append('pageIndex', String(opts.pageIndex))
    form.append('x', String(opts.x))
    form.append('y', String(opts.y))
    if (opts.width) form.append('width', String(opts.width))
    if (opts.kind) form.append('kind', opts.kind)
    return request<{ object: PlacedObject; session: SessionInfo }>(
      `/api/documents/${id}/objects/image`, { method: 'POST', body: form },
    )
  },

  /** Drag, resize, rotate or retype a placed object. */
  updateObject: (
    id: string,
    objectId: string,
    patch: Partial<Pick<PlacedObject, 'x' | 'y' | 'width' | 'height' | 'rotation' | 'text' | 'opacity'>> &
      { fontSize?: number },
  ) =>
    request<{ object: PlacedObject; session: SessionInfo }>(
      `/api/documents/${id}/objects/${objectId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      },
    ),

  deleteObject: (id: string, objectId: string) =>
    request<{ session: SessionInfo }>(`/api/documents/${id}/objects/${objectId}`, {
      method: 'DELETE',
    }),

  // --- existing content --------------------------------------------------

  paragraphs: (id: string, pageIndex: number) =>
    request<{ paragraphs: Paragraph[] }>(
      `/api/documents/${id}/pages/${pageIndex}/paragraphs`,
    ),

  deleteRuns: (id: string, pageIndex: number, indices: number[]) =>
    json<{ report: EditReport; session: SessionInfo }>(
      `/api/documents/${id}/pages/${pageIndex}/delete-runs`, { indices },
    ),

  moveRun: (id: string, pageIndex: number, runIndex: number, dx: number, dy: number) =>
    json<{ report: EditReport; session: SessionInfo }>(
      `/api/documents/${id}/move-run`, { pageIndex, runIndex, dx, dy },
    ),

  // --- reorganise --------------------------------------------------------

  duplicatePages: (id: string, indices: number[], times = 1) =>
    json<{ created: number[]; session: SessionInfo }>(
      `/api/documents/${id}/pages/duplicate`, { indices, times },
    ),

  insertBlank: (id: string, at: number, count = 1, like?: number) =>
    json<{ created: number[]; session: SessionInfo }>(
      `/api/documents/${id}/pages/insert-blank`, { at, count, like },
    ),

  /** Move pages so they sit together starting at `to` (original numbering). */
  movePages: (id: string, indices: number[], to: number) =>
    json<{ landed: number[]; session: SessionInfo }>(
      `/api/documents/${id}/pages/move`, { indices, to },
    ),

  // --- extraction --------------------------------------------------------

  listImages: (id: string, pages?: number[]) =>
    json<{ images: ExtractedImageInfo[]; count: number; totalBytes: number }>(
      `/api/documents/${id}/extract/images/list`, { pages },
    ),

  /** ZIP of every image, plus a manifest. JPEGs come out byte-identical. */
  downloadImages: (id: string, pages?: number[]) =>
    blob(`/api/documents/${id}/extract/images`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pages }),
    }),

  extractText: (id: string, pages?: number[], includeInvisible = true) =>
    json<{ pages: ExtractedTextPage[]; characters: number }>(
      `/api/documents/${id}/extract/text`, { pages, includeInvisible },
    ),

  downloadText: (id: string, fmt: 'txt' | 'json', pages?: number[]) =>
    blob(`/api/documents/${id}/extract/text/download?fmt=${fmt}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pages }),
    }),

  // --- object stacking ---------------------------------------------------

  arrangeObject: (id: string, objectId: string, action: ArrangeAction) =>
    json<{ order: string[]; session: SessionInfo }>(
      `/api/documents/${id}/objects/${objectId}/arrange`, { action },
    ),

  // --- comments ----------------------------------------------------------

  comments: (id: string, pageIndex?: number) =>
    request<{ threads: CommentThread[]; count: number; orphanReplies: Comment[] }>(
      `/api/documents/${id}/comments` +
        (pageIndex === undefined ? '' : `?pageIndex=${pageIndex}`),
    ),

  addComment: (
    id: string,
    body: {
      pageIndex: number
      kind: CommentKind
      rect: Rect
      contents?: string
      quads?: Rect[]
      inReplyTo?: string
      color?: [number, number, number]
      icon?: string
      /** Pen strokes, each [x1, y1, x2, y2, ...]. */
      ink?: number[][]
      points?: number[]
      width?: number
      arrow?: boolean
      stamp?: StampKind
      stampDetail?: string
    },
  ) =>
    json<{ comment: Comment; session: SessionInfo }>(
      `/api/documents/${id}/comments`, body,
    ),

  updateComment: (
    id: string,
    commentId: string,
    patch: { contents?: string; rect?: Rect; color?: [number, number, number] },
  ) =>
    request<{ comment: Comment; session: SessionInfo }>(
      `/api/documents/${id}/comments/${commentId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      },
    ),

  deleteComment: (id: string, commentId: string) =>
    request<{ removed: number; session: SessionInfo }>(
      `/api/documents/${id}/comments/${commentId}`, { method: 'DELETE' },
    ),

  // --- crop --------------------------------------------------------------

  contentBounds: (id: string, pageIndex: number, margin = 6) =>
    request<ContentBounds>(
      `/api/documents/${id}/pages/${pageIndex}/content-bounds?margin=${margin}`,
    ),

  /**
   * Crop pages to a region.
   *
   * `destructive: false` only sets the page boxes -- the trimmed content stays
   * in the file and anyone can recover it by widening them. `true` removes it.
   */
  crop: (id: string, indices: number[], rect: Rect, destructive = false) =>
    json<CropResult>(`/api/documents/${id}/pages/crop`, {
      indices,
      rect,
      destructive,
    }),

  // --- split -------------------------------------------------------------

  splitPreview: (id: string, spec: SplitSpec) =>
    json<SplitPreview>(`/api/documents/${id}/split/preview`, spec),

  /** Returns a ZIP of the resulting documents. The session is unchanged. */
  split: (id: string, spec: SplitSpec) =>
    blob(`/api/documents/${id}/split`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(spec),
    }),

  // --- OCR ---------------------------------------------------------------

  ocrStatus: (id: string) => request<OcrStatus>(`/api/documents/${id}/ocr/status`),

  ocr: (
    id: string,
    pageIndex: number,
    opts: { applyLayer?: boolean; engine?: string; crossCheck?: boolean } = {},
  ) =>
    json<{ result: OcrResult; linesApplied: number; session: SessionInfo }>(
      `/api/documents/${id}/ocr`,
      { pageIndex, applyLayer: opts.applyLayer ?? true, ...opts },
    ),

  // --- Redaction ---------------------------------------------------------

  redactionPatterns: () =>
    request<{ patterns: RedactionPattern[] }>('/api/documents/redact/patterns'),

  /** Find sensitive values without changing the document. */
  redactScan: (id: string, opts: { pages?: number[]; patterns?: string[] } = {}) =>
    json<{ regions: RedactionRegion[]; count: number }>(
      `/api/documents/${id}/redact/scan`, opts,
    ),

  redact: (
    id: string,
    pageIndex: number,
    body: {
      rects?: { x0: number; y0: number; x1: number; y1: number }[]
      auto?: RedactionRegion[]
    },
  ) =>
    json<{
      report: RedactionReport
      objectsRemoved: number
      requiresFlatten: boolean
      session: SessionInfo
    }>(`/api/documents/${id}/redact`, { pageIndex, ...body }),

  redactVerify: (id: string) =>
    request<RedactionVerdict>(`/api/documents/${id}/redact/verify`),

  /** Sanitised, flattened, verified bytes. Refused if anything still leaks. */
  /** The redacted document as Save writes it, with what sanitising removed
   * (the server's X-Odie-Sanitized list) attached. */
  /** Make text boxes, images, signatures, form answers and comments part of the pages. */
  flatten: (id: string) =>
    json<{ objects: number; fields: number; comments: number; session: SessionInfo }>(
      `/api/documents/${id}/flatten`, {},
    ),

  /** Digital whiteout: remove everything inside the boxes and paint them white. */
  whiteout: (id: string, pageIndex: number, rects: { x0: number; y0: number; x1: number; y1: number }[]) =>
    json<{ report: RedactionReport; objectsRemoved: number; session: SessionInfo }>(
      `/api/documents/${id}/whiteout`, { pageIndex, rects },
    ),

  fileFlattened: async (id: string): Promise<Blob & { sanitized?: string }> => {
    let sanitized = ''
    const data = await blob(`/api/documents/${id}/file/flattened`, {}, (headers) => {
      sanitized = headers.get('X-Odie-Sanitized') ?? ''
    })
    return Object.assign(data, { sanitized })
  },

  // --- forms --------------------------------------------------------------

  form: (id: string) => request<FormState>(`/api/documents/${id}/form`),

  fillForm: (id: string, values: Record<string, string | string[] | boolean | null>) =>
    json<FormState & { problems: string[]; session: SessionInfo }>(
      `/api/documents/${id}/form`, { values },
    ),

  resetForm: (id: string) =>
    json<FormState & { reset: number; session: SessionInfo }>(`/api/documents/${id}/form/reset`, {}),

  flattenForm: (id: string) =>
    json<FormState & { flattened: number; session: SessionInfo }>(`/api/documents/${id}/form/flatten`, {}),

  // --- file size ------------------------------------------------------------

  compress: (id: string, level: CompressLevel) =>
    json<CompressReport>(`/api/documents/${id}/compress`, { level }),

  // --- bookmarks and links ------------------------------------------------------

  outline: (id: string) => request<{ bookmarks: Bookmark[] }>(`/api/documents/${id}/outline`),

  setOutline: (id: string, bookmarks: Bookmark[]) =>
    request<{ count: number; bookmarks: Bookmark[]; session: SessionInfo }>(
      `/api/documents/${id}/outline`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookmarks }),
      }),

  links: (id: string) => request<{ links: PdfLink[] }>(`/api/documents/${id}/links`),

  addLink: (id: string, link: {
    pageIndex: number
    rect: [number, number, number, number]
    uri?: string
    targetPage?: number
    top?: number | null
  }) => json<{ id: string; links: PdfLink[]; session: SessionInfo }>(`/api/documents/${id}/links`, link),

  deleteLink: (id: string, linkId: string) =>
    request<{ links: PdfLink[]; session: SessionInfo }>(
      `/api/documents/${id}/links/${encodeURIComponent(linkId)}`, { method: 'DELETE' },
    ),

  // --- rewrap -------------------------------------------------------------------

  rewrap: (id: string, pageIndex: number, runIndices: number[], text: string) =>
    json<{ report: RewrapReport; session: SessionInfo }>(
      `/api/documents/${id}/pages/${pageIndex}/rewrap`, { runIndices, text },
    ),

  // --- signatures -----------------------------------------------------------------

  signatures: (id: string) => request<SignatureReport>(`/api/documents/${id}/signatures`),

  checkCertificate: (id: string, certificate: Blob, password: string) => {
    const form = new FormData()
    form.append('certificate', certificate, 'certificate.pfx')
    form.append('password', password)
    return request<{ signer: string; email: string; issuer: string; validTo: string }>(
      `/api/documents/${id}/sign/certificate`, { method: 'POST', body: form },
    )
  },

  sign: (id: string, certificate: Blob, options: {
    password: string; reason?: string; location?: string; contact?: string
    fieldName?: string; pageIndex?: number; rect?: [number, number, number, number] | null
  }) => {
    const form = new FormData()
    form.append('certificate', certificate, 'certificate.pfx')
    form.append('password', options.password)
    form.append('reason', options.reason ?? '')
    form.append('location', options.location ?? '')
    form.append('contact', options.contact ?? '')
    if (options.fieldName) form.append('fieldName', options.fieldName)
    form.append('pageIndex', String(options.pageIndex ?? 0))
    if (options.rect) form.append('rect', JSON.stringify(options.rect))
    return request<SignatureReport & { session: SessionInfo }>(
      `/api/documents/${id}/sign`, { method: 'POST', body: form },
    )
  },

  // --- compare --------------------------------------------------------------------

  compare: (id: string, other: Blob, name: string, options: { otherIsNewer?: boolean; password?: string } = {}) => {
    const form = new FormData()
    form.append('file', other, name)
    form.append('otherIsNewer', String(Boolean(options.otherIsNewer)))
    if (options.password) form.append('password', options.password)
    return request<CompareResult>(`/api/documents/${id}/compare`, { method: 'POST', body: form })
  },

  // --- ask Swifty -------------------------------------------------------------------

  /** Streams Swifty's answer; ``onEvent`` is called for each line as it arrives. */
  async ask(
    id: string,
    body: { question?: string; summary?: boolean; history?: { role: string; content: string }[] },
    onEvent: (event: AskEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const token = await tokenProvider()
    const headers = new Headers({ 'Content-Type': 'application/json' })
    if (token) headers.set('Authorization', `Bearer ${token}`)
    const response = await fetch(`/api/documents/${id}/ask`, {
      method: 'POST', headers, body: JSON.stringify(body), signal,
    })
    if (!response.ok || !response.body) {
      let message = `${response.status} ${response.statusText}`
      try {
        const detail = (await response.json())?.detail
        message = typeof detail === 'string' ? detail : detail?.message ?? message
      } catch {
        // not JSON
      }
      throw new ApiError(response.status, message)
    }
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffered = ''
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buffered += decoder.decode(value, { stream: true })
      let newline: number
      while ((newline = buffered.indexOf('\n')) >= 0) {
        const line = buffered.slice(0, newline).trim()
        buffered = buffered.slice(newline + 1)
        if (line) onEvent(JSON.parse(line) as AskEvent)
      }
    }
    if (buffered.trim()) onEvent(JSON.parse(buffered) as AskEvent)
  },

  // --- measuring ------------------------------------------------------------------

  scales: (id: string) =>
    request<{ pages: { pageIndex: number; scales: PageScale[] }[]; units: string[] }>(
      `/api/documents/${id}/scales`),

  setScale: (id: string, body: {
    pages?: number[]; paper?: number; paperUnit?: string; real?: number; realUnit?: string
    calibratePoints?: number[]; remove?: boolean
  }) => request<{ pages: { pageIndex: number; scales: PageScale[] }[]; session: SessionInfo }>(
    `/api/documents/${id}/scale`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }),

  measure: (id: string, pageIndex: number, kind: MeasureKind, points: number[]) =>
    json<{ measurement: { id: string; label: string; value: number }; session: SessionInfo }>(
      `/api/documents/${id}/measurements`, { pageIndex, kind, points }),

  // --- building forms -------------------------------------------------------------

  addField: (id: string, field: NewField) =>
    json<FormState & { name: string; session: SessionInfo }>(`/api/documents/${id}/form/fields`, field),

  addFields: (id: string, fields: NewField[]) =>
    json<FormState & { names: string[]; session: SessionInfo }>(`/api/documents/${id}/form/fields/batch`, { fields }),

  updateField: (id: string, name: string, changes: Record<string, unknown>) =>
    json<FormState & { name: string; session: SessionInfo }>(
      `/api/documents/${id}/form/fields/update`, { name, changes }),

  deleteField: (id: string, name: string) =>
    json<FormState & { session: SessionInfo }>(`/api/documents/${id}/form/fields/delete`, { name }),

  detectFields: (id: string, pageIndex: number) =>
    request<{ suggestions: FieldSuggestion[] }>(`/api/documents/${id}/pages/${pageIndex}/form/detect`),

  // --- accessibility ----------------------------------------------------------------

  accessibility: (id: string) => request<AccessReport>(`/api/documents/${id}/accessibility`),

  structure: (id: string) =>
    request<{ blocks: StructureBlock[]; types: string[] }>(`/api/documents/${id}/accessibility/structure`),

  setAccessProperties: (id: string, body: { title?: string; language?: string }) =>
    json<AccessReport & { session: SessionInfo }>(`/api/documents/${id}/accessibility/properties`, body),

  fixTabOrder: (id: string) =>
    json<AccessReport & { fixed: number; session: SessionInfo }>(`/api/documents/${id}/accessibility/tabs`, {}),

  setTooltips: (id: string, values: Record<string, string>) =>
    json<AccessReport & { session: SessionInfo }>(`/api/documents/${id}/accessibility/tooltips`, { values }),

  autotag: (id: string, language: string) =>
    json<AccessReport & { counts: Record<string, number>; session: SessionInfo }>(
      `/api/documents/${id}/accessibility/autotag`, { language }),

  changeBlock: (id: string, blockId: string, change: { type?: string; alt?: string }) =>
    json<{ blocks: StructureBlock[]; session: SessionInfo }>(
      `/api/documents/${id}/accessibility/element`, { id: blockId, ...change }),

  moveBlock: (id: string, blockId: string, delta: number) =>
    json<{ id: string; blocks: StructureBlock[]; session: SessionInfo }>(
      `/api/documents/${id}/accessibility/element/move`, { id: blockId, delta }),

  blockToArtifact: (id: string, blockId: string) =>
    json<{ blocks: StructureBlock[]; session: SessionInfo }>(
      `/api/documents/${id}/accessibility/element/artifact`, { id: blockId }),

  close: (id: string) =>
    request<{ closed: boolean }>(`/api/documents/${id}`, { method: 'DELETE' }),
}
